/**
 * Visor publicado de un dashboard del Creador (páginas, filtros, widgets).
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout/legacy';
import WidgetRenderer from './admin/widget/WidgetRenderer';
import DashboardFilterBar from './admin/widget/DashboardFilterBar';
import { engineFetch, backendFetch, engineErrorMessage, primeQueryCache, clearQueryCache, bumpEngineDataVersion, refreshEngineSources, DEFAULT_CANVAS, SURFACE, getWidgetStyle, getBlockFrameStyle, isContainerType, normalizeReportPages, firstPageDataSource, normalizeDataSources, applySourceIdToWidgets, sourceRequestFields, viewerArtboardStyle, widgetCardChrome, RGL_BREAKPOINTS, rglCols } from './admin/widget/engineConfig';
import { widgetZ } from './admin/widget/arrange';
import { buildEngineFilters } from './admin/widget/filterUtils';
import { exportElementToPdf, sanitizeFilename } from './admin/widget/exportUtils';
import ReportPageSidebar from './admin/widget/ReportPageSidebar';
import { viewerPages } from './admin/widget/pageNav';
import { DashboardThemeContext, normalizeReportTheme } from './admin/widget/dashboardTheme';
import { stackWidgets, stackedHeight } from './admin/widget/stackLayout';
import { useIsNarrow } from '../useMediaQuery';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './admin/widget/creatorCanvas.css';

const ResponsiveGridLayout = WidthProvider(Responsive);
// Mientras el reporte está abierto y visible, los datos se refrescan solos con esta cadencia.
const AUTO_REFRESH_MS = 5 * 60 * 1000;

const parseCustomConfig = (report) => {
    const raw = JSON.parse(report.customConfig);
    if (raw.pages || (raw.layout && raw.widgets)) {
        const { pages, activePageId, sections } = normalizeReportPages(raw);
        const { dataSources, joins } = normalizeDataSources(raw);
        const legacyId = dataSources[0]?.id;
        return {
            dataSource: raw.dataSource || firstPageDataSource(raw),
            dataSources,
            joins,
            theme: normalizeReportTheme(raw.theme),
            schemas: raw.schemas || {},
            pages: pages.map(page => ({
                ...page,
                widgets: applySourceIdToWidgets(page.widgets || [], legacyId)
            })),
            sections,
            activePageId
        };
    }
    const fallback = normalizeReportPages({
        widgets: [{
            id: 'main',
            type: 'table',
            title: report.reporteNombre,
            query: raw
        }],
        layout: [{ i: 'main', x: 0, y: 0, w: 12, h: 8 }]
    });
    const { dataSources, joins } = normalizeDataSources({
        dataSource: {
            spreadsheet_id: raw.spreadsheet_id || '',
            sheet_name: raw.sheet_name || ''
        },
        widgets: fallback.pages?.[0]?.widgets || []
    });
    return {
        dataSource: {
            spreadsheet_id: raw.spreadsheet_id || '',
            sheet_name: raw.sheet_name || ''
        },
        dataSources,
        joins,
        schemas: {},
        filters: [],
        ...fallback
    };
};

const formatSnapshotDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const CustomReportRenderer = ({ report, useSnapshot = true }) => {
    const [pages, setPages] = useState([]);
    const [sections, setSections] = useState([]);
    const [activePageId, setActivePageId] = useState('');
    const [dataSources, setDataSources] = useState([]);
    const [joins, setJoins] = useState([]);
    const [schemasBySource, setSchemasBySource] = useState({});
    const [savedSchemas, setSavedSchemas] = useState({});
    const [reportTheme, setReportTheme] = useState('classic');
    const [filterValues, setFilterValues] = useState({});
    const [schemaColumns, setSchemaColumns] = useState([]);
    const [dataSource, setDataSource] = useState({ spreadsheet_id: '', sheet_name: '' });
    const [error, setError] = useState(null);
    const [schemaError, setSchemaError] = useState(null);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [snapshotReady, setSnapshotReady] = useState(false);
    const [snapshotAt, setSnapshotAt] = useState(null);
    const [refreshingData, setRefreshingData] = useState(false);
    const [lastRefreshAt, setLastRefreshAt] = useState(null);
    const refreshingRef = useRef(false);
    const lastRefreshRef = useRef(Date.now());
    const refreshDataRef = useRef(null);
    const dashboardRef = useRef(null);
    const narrow = useIsNarrow();

    useEffect(() => {
        if (!report?.customConfig) return;
        try {
            const parsed = parseCustomConfig(report);
            setPages(parsed.pages || []);
            setSections(parsed.sections || []);
            const visible = viewerPages(parsed.pages || [], parsed.sections || []);
            const preferred = visible.find(page => page.id === parsed.activePageId) || visible[0] || parsed.pages?.[0];
            setActivePageId(preferred?.id || '');
            setDataSources(parsed.dataSources || []);
            setJoins(parsed.joins || []);
            setDataSource(parsed.dataSource);
            setSavedSchemas(parsed.schemas || {});
            setReportTheme(normalizeReportTheme(parsed.theme));
            setFilterValues({});
            setError(null);
            setSchemaError(null);
        } catch (e) {
            setError('Error al cargar la configuración del reporte. El formato es inválido.');
            console.error('Error parsing customConfig:', e);
        }
    }, [report?.customConfig, report?.reporteNombre]);

    const activePage = pages.find(page => page.id === activePageId) || pages[0];
    const layout = activePage?.layout || [];
    const widgets = activePage?.widgets || [];
    const canvas = { ...DEFAULT_CANVAS, ...(activePage?.canvas || {}) };
    const filterDefs = activePage?.filters || [];
    const navPages = viewerPages(pages, sections);
    const sectionName = (page) => sections.find(section => section.id === page.sectionId)?.name;

    useEffect(() => {
        // Las columnas guardadas al publicar sirven para pintar la barra de filtros de
        // inmediato; solo se consulta al motor por las fuentes que no las tengan.
        const loadSchemas = async () => {
            const catalog = { dataSources, joins };
            const usable = dataSources.filter(source => (
                source.kind === 'join' || (source.spreadsheet_id && source.sheet_name)
            ));
            const next = {};
            usable.forEach(source => {
                const saved = savedSchemas[source.id];
                if (saved?.columns?.length) next[source.id] = saved;
            });
            const missing = usable.filter(source => !next[source.id]);
            if (Object.keys(next).length) {
                setSchemasBySource({ ...next });
                setSchemaColumns(next[dataSources[0]?.id]?.columns || []);
            }

            const failures = [];
            const fetched = await Promise.all(missing.map(async (source) => {
                try {
                    const response = await engineFetch('/api/engine/schema', {
                        method: 'POST',
                        body: JSON.stringify(sourceRequestFields(source, catalog, { includeSelection: false }))
                    });
                    if (!response.ok) {
                        failures.push(await engineErrorMessage(response, 'No se pudo leer una fuente.'));
                        return null;
                    }
                    const res = await response.json();
                    if (!res.success) {
                        failures.push(res.detail || res.error || 'La fuente no devolvió columnas.');
                        return null;
                    }
                    return [source.id, { columns: res.columns || [], rowCount: res.row_count }];
                } catch (e) {
                    failures.push(e.message || 'No se pudo cargar el esquema de filtros.');
                    return null;
                }
            }));
            fetched.filter(Boolean).forEach(([id, info]) => { next[id] = info; });

            setSchemasBySource(next);
            setSchemaColumns(next[dataSources[0]?.id]?.columns || []);
            setSchemaError(failures.length ? failures[0] : null);
        };
        if (dataSources.length) loadSchemas();
    }, [dataSources, joins, savedSchemas]);

    const engineFilters = useMemo(
        () => buildEngineFilters(filterDefs, filterValues),
        [filterDefs, filterValues]
    );

    // Snapshot de la página: se pinta con los datos de la última publicación o refresco
    // y solo se consulta al motor cuando el usuario cambia un filtro.
    useEffect(() => {
        const pageId = activePage?.id;
        if (!useSnapshot || !report?.id || !pageId) {
            setSnapshotReady(true);
            return undefined;
        }
        let alive = true;
        setSnapshotReady(false);
        setSnapshotAt(null);
        (async () => {
            try {
                const response = await backendFetch(
                    `/api/reports/${report.id}/snapshot?pageId=${encodeURIComponent(pageId)}`
                );
                if (!alive) return;
                if (response.ok) {
                    const data = await response.json();
                    primeQueryCache(data.queries || [], data.updatedAt);
                    setSnapshotAt(data.updatedAt || null);
                }
            } catch (e) {
                // Sin snapshot el visor sigue funcionando: los widgets consultan al motor.
            } finally {
                if (alive) setSnapshotReady(true);
            }
        })();
        return () => { alive = false; };
    }, [report?.id, activePage?.id, useSnapshot]);

    /**
     * Relee las hojas de Drive y repinta los widgets en sitio.
     * Con snapshot: el backend recalcula la página (en automático, solo si tiene más de 5 min).
     * Sin snapshot: se pide al motor descartar su copia y los widgets vuelven a consultar.
     * El automático no muestra errores: el siguiente ciclo lo vuelve a intentar.
     */
    const refreshData = async ({ auto = false } = {}) => {
        if (refreshingRef.current) return;
        refreshingRef.current = true;
        lastRefreshRef.current = Date.now();
        if (!auto) setRefreshingData(true);
        try {
            const pageId = activePage?.id;
            if (useSnapshot && report?.id && pageId && snapshotAt) {
                const response = await backendFetch(
                    `/api/reports/${report.id}/snapshot/refresh?pageId=${encodeURIComponent(pageId)}${auto ? '&auto=1' : ''}`,
                    { method: 'POST' }
                );
                if (!response.ok) {
                    throw new Error(await engineErrorMessage(response, 'No se pudieron actualizar los datos.'));
                }
                const fresh = await backendFetch(
                    `/api/reports/${report.id}/snapshot?pageId=${encodeURIComponent(pageId)}`
                );
                if (fresh.ok) {
                    const data = await fresh.json();
                    // En automático, si nadie recalculó la página no hay nada que repintar.
                    if (auto && data.updatedAt && data.updatedAt === snapshotAt) return;
                    clearQueryCache();
                    primeQueryCache(data.queries || [], data.updatedAt);
                    setSnapshotAt(data.updatedAt || null);
                } else {
                    clearQueryCache();
                }
            } else {
                await refreshEngineSources(dataSources, { maxAgeSeconds: auto ? (AUTO_REFRESH_MS / 1000) - 20 : 0 });
                clearQueryCache();
                setLastRefreshAt(new Date().toISOString());
            }
            bumpEngineDataVersion();
            // Las columnas y valores guardados al publicar ya pueden no estar completos:
            // la barra de filtros se vuelve a armar con lo que tiene ahora la hoja.
            setSavedSchemas({});
        } catch (err) {
            if (!auto) alert(err.message || 'No se pudieron actualizar los datos.');
            else console.warn('Refresco automático del reporte falló:', err.message);
        } finally {
            refreshingRef.current = false;
            if (!auto) setRefreshingData(false);
        }
    };
    refreshDataRef.current = refreshData;

    // Refresco automático: cada 5 min con la pestaña visible; al volver a una pestaña
    // que estuvo oculta más de ese tiempo, se refresca de inmediato.
    useEffect(() => {
        if (!report?.id) return undefined;
        lastRefreshRef.current = Date.now();
        const due = () => Date.now() - lastRefreshRef.current >= AUTO_REFRESH_MS;
        const tick = () => {
            if (document.visibilityState === 'visible' && due()) {
                refreshDataRef.current?.({ auto: true });
            }
        };
        const timer = window.setInterval(tick, 30 * 1000);
        document.addEventListener('visibilitychange', tick);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [report?.id]);

    const handleExportReportPdf = async () => {
        if (!dashboardRef.current || exportingPdf) return;
        setExportingPdf(true);
        try {
            await exportElementToPdf(
                dashboardRef.current,
                sanitizeFilename(`${report?.reporteNombre || 'reporte'} ${activePage?.name || ''}`.trim()),
                {
                    landscape: true,
                    backgroundColor: canvas.background || SURFACE.canvas,
                    title: report?.reporteNombre || 'Reporte',
                    subtitle: pages.length > 1 ? activePage?.name : ''
                }
            );
        } catch (err) {
            alert(err?.message || 'No se pudo generar el PDF del reporte.');
        } finally {
            setExportingPdf(false);
        }
    };

    if (error) {
        return <div style={styles.errorContainer}>{error}</div>;
    }

    if (!report) {
        return <div style={styles.container}>Cargando reporte...</div>;
    }

    if (!report.customConfig) {
        return (
            <div style={styles.errorContainer}>
                Este reporte no tiene una configuración guardada. Ábrelo en el Creador y vuelve a publicarlo.
            </div>
        );
    }

    return (
        <DashboardThemeContext.Provider value={reportTheme}>
        <div
            ref={dashboardRef}
            style={{
                ...styles.container,
                ...(narrow ? { padding: '12px' } : null),
                backgroundColor: SURFACE.page
            }}
        >
            <div style={styles.headerRow}>
                <h2 style={narrow ? { ...styles.title, fontSize: '1.15rem', flexBasis: '100%' } : styles.title}>{report.reporteNombre}</h2>
                <div style={narrow ? { ...styles.headerActions, ...styles.headerActionsNarrow } : styles.headerActions} data-export-ignore="true">
                    {(snapshotAt || lastRefreshAt) && (
                        <span style={styles.dataAge} title="Se actualiza solo cada 5 minutos mientras el reporte está abierto.">
                            Datos del {formatSnapshotDate(snapshotAt || lastRefreshAt)}
                        </span>
                    )}
                    <button
                        type="button"
                        style={narrow ? { ...styles.refreshBtn, minHeight: 40 } : styles.refreshBtn}
                        disabled={refreshingData}
                        onClick={() => refreshData()}
                        title="Vuelve a leer las hojas de Drive conectadas"
                    >
                        {refreshingData ? 'Actualizando…' : 'Actualizar'}
                    </button>
                    <button
                        type="button"
                        style={narrow ? { ...styles.pdfBtn, minHeight: 40 } : styles.pdfBtn}
                        disabled={exportingPdf}
                        onClick={handleExportReportPdf}
                    >
                        {exportingPdf ? 'Generando PDF…' : 'Exportar PDF'}
                    </button>
                </div>
            </div>
            <div style={narrow ? { ...styles.stage, flexDirection: 'column', gap: '12px' } : styles.stage}>
                {narrow ? (
                    navPages.length > 1 && (
                        <label style={styles.pageSelectWrap} data-export-ignore="true">
                            <span style={styles.pageSelectLabel}>Hoja</span>
                            <select
                                value={activePage?.id || ''}
                                onChange={(event) => setActivePageId(event.target.value)}
                                style={styles.pageSelect}
                            >
                                {navPages.map(page => (
                                    <option key={page.id} value={page.id}>
                                        {sectionName(page) ? `${sectionName(page)} · ${page.name}` : page.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )
                ) : (
                <div data-export-ignore="true">
                    <ReportPageSidebar
                        pages={pages}
                        sections={sections}
                        activePageId={activePage?.id}
                        onSelect={setActivePageId}
                    />
                </div>
                )}
                <div style={styles.stageMain}>
            {schemaError && (
                <div style={styles.schemaError}>{schemaError}</div>
            )}
            {filterDefs.some(def => def.column && !String(def.id).startsWith('slicer-')) && (
                <div style={{ marginBottom: '16px' }}>
                    <DashboardFilterBar
                        elevated
                        filterDefs={filterDefs}
                        filterValues={filterValues}
                        schemaColumns={schemaColumns.length ? schemaColumns : filterDefs.map(def => ({
                            name: def.column,
                            values: def.options || []
                        }))}
                        schemasBySource={schemasBySource}
                        dataSources={dataSources}
                        joins={joins}
                        dataSource={sourceRequestFields(dataSources[0] || dataSource, { dataSources, joins })}
                        onValuesChange={setFilterValues}
                    />
                </div>
            )}
            <div style={{
                ...viewerArtboardStyle(canvas),
                width: canvas.widthMode === 'px' && !narrow ? canvas.width : '100%',
                minHeight: narrow ? 0 : canvas.height,
                margin: canvas.widthMode === 'px' && !narrow ? '0 auto' : undefined
            }}>
            {!snapshotReady ? (
                <div style={styles.loadingBox}>Cargando datos del reporte…</div>
            ) : narrow ? (
                /* Móvil: una columna, widgets en orden de lectura (arriba→abajo, izq→der). */
                <div style={styles.stackColumn}>
                    {stackWidgets(widgets, layout).map(({ widget: widgetConfig, item }) => {
                        const blockFrame = isContainerType(widgetConfig.type)
                            ? getBlockFrameStyle(getWidgetStyle(widgetConfig))
                            : null;
                        const cardBg = widgetConfig.style?.cardBackground
                            || (isContainerType(widgetConfig.type) ? '#f4eefc' : SURFACE.card);
                        return (
                            <div key={widgetConfig.id} style={{
                                ...styles.widgetWrapper,
                                ...widgetCardChrome({
                                    backgroundColor: cardBg,
                                    borderRadius: widgetConfig.style?.cardRadius ?? 10,
                                }, reportTheme),
                                overflow: widgetConfig.type === 'text' ? 'auto' : 'hidden',
                                height: stackedHeight(widgetConfig, item, canvas.rowHeight),
                                ...(blockFrame ? { border: blockFrame.border, boxSizing: 'border-box' } : {}),
                            }}>
                                <WidgetRenderer
                                    stacked
                                    config={widgetConfig}
                                    canvasRowHeight={canvas.rowHeight}
                                    engineFilters={engineFilters}
                                    showTitle={widgetConfig.style?.showTitle !== false}
                                    filterValues={filterValues}
                                    schemaColumns={schemasBySource[widgetConfig.query?.dataSourceId]?.columns || schemaColumns}
                                    schemasBySource={schemasBySource}
                                    filterDefs={filterDefs}
                                    dataSources={dataSources}
                                    joins={joins}
                                    onFilterValueChange={(id, vals) => setFilterValues(prev => ({ ...prev, [id]: vals }))}
                                    exportable
                                />
                            </div>
                        );
                    })}
                </div>
            ) : (
            <ResponsiveGridLayout
                className="layout"
                layouts={{ lg: layout }}
                breakpoints={RGL_BREAKPOINTS}
                cols={rglCols(canvas.columns)}
                rowHeight={canvas.rowHeight}
                margin={[8, 8]}
                containerPadding={[12, 12]}
                compactType={null}
                allowOverlap
                isDraggable={false}
                isResizable={false}
            >
                {widgets.map(widgetConfig => {
                    const blockFrame = isContainerType(widgetConfig.type)
                        ? getBlockFrameStyle(getWidgetStyle(widgetConfig))
                        : null;
                    const cardBg = widgetConfig.style?.cardBackground
                        || (isContainerType(widgetConfig.type) ? '#f4eefc' : SURFACE.card);
                    return (
                    <div key={widgetConfig.id} style={{
                        ...styles.widgetWrapper,
                        ...widgetCardChrome({
                            backgroundColor: cardBg,
                            borderRadius: widgetConfig.style?.cardRadius ?? 10,
                        }, reportTheme),
                        overflow: 'visible',
                        zIndex: widgetZ(widgetConfig) + 1,
                        ...(blockFrame ? { margin: blockFrame.margin, border: blockFrame.border, boxSizing: 'border-box' } : {}),
                    }}>
                        <WidgetRenderer
                            config={widgetConfig}
                            canvasRowHeight={canvas.rowHeight}
                            engineFilters={engineFilters}
                            showTitle={widgetConfig.style?.showTitle !== false}
                            filterValues={filterValues}
                            schemaColumns={schemasBySource[widgetConfig.query?.dataSourceId]?.columns || schemaColumns}
                            schemasBySource={schemasBySource}
                            filterDefs={filterDefs}
                            dataSources={dataSources}
                            joins={joins}
                            onFilterValueChange={(id, vals) => setFilterValues(prev => ({ ...prev, [id]: vals }))}
                            exportable
                        />
                    </div>
                    );
                })}
            </ResponsiveGridLayout>
            )}
            </div>
                </div>
            </div>
        </div>
        </DashboardThemeContext.Provider>
    );
};

const styles = {
    container: {
        padding: '20px',
        backgroundColor: SURFACE.page,
        height: '100%',
        overflow: 'auto'
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '16px',
        flexWrap: 'wrap'
    },
    stage: {
        display: 'flex',
        alignItems: 'stretch',
        gap: '16px',
        minHeight: 0
    },
    stageMain: {
        flex: 1,
        minWidth: 0
    },
    title: {
        color: SURFACE.ink,
        margin: 0,
        flex: 1,
        minWidth: 0
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0
    },
    headerActionsNarrow: {
        flexWrap: 'wrap',
        flexShrink: 1,
        width: '100%'
    },
    stackColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '10px'
    },
    pageSelectWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    pageSelectLabel: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#5f6368',
        textTransform: 'uppercase',
        letterSpacing: '0.06em'
    },
    pageSelect: {
        flex: 1,
        minWidth: 0,
        minHeight: 40,
        padding: '8px 10px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#d1c4e9',
        borderRadius: '8px',
        backgroundColor: '#fff',
        color: '#1c1d21',
        fontSize: 16,
        fontFamily: 'inherit'
    },
    dataAge: {
        color: '#5f6368',
        fontSize: '0.78rem'
    },
    refreshBtn: {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#d1c4e9',
        backgroundColor: '#fff',
        color: '#5E35B1',
        borderRadius: '8px',
        padding: '8px 14px',
        fontWeight: 600,
        fontSize: '0.85rem',
        cursor: 'pointer',
        flexShrink: 0
    },
    loadingBox: {
        padding: '24px',
        color: '#5f6368',
        fontSize: '0.9rem'
    },
    pdfBtn: {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#d1c4e9',
        backgroundColor: '#5E35B1',
        color: '#fff',
        borderRadius: '8px',
        padding: '8px 14px',
        fontWeight: 600,
        fontSize: '0.85rem',
        cursor: 'pointer',
        flexShrink: 0
    },
    widgetWrapper: {
        backgroundColor: SURFACE.card,
        borderRadius: '10px',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    schemaError: {
        backgroundColor: '#fce8e6',
        color: '#c5221f',
        padding: '10px 12px',
        borderRadius: 8,
        marginBottom: 12,
        fontSize: '0.85rem',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#f28b82',
    },
    errorContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#c5221f',
        fontSize: '1.05rem',
        padding: 24,
        textAlign: 'center'
    }
};

export default CustomReportRenderer;
